import {
  STATUS,
  formatarData,
  getStatusConfig,
  statusDoMembro,
  temCoordenadas,
} from './analiseFrequenciaHelpers';

/** Paleta institucional da análise territorial */
const C = {
  navy: '1E3A5F',
  navyDark: '0F2744',
  teal: '0F766E',
  tealLight: 'CCFBF1',
  white: 'FFFFFF',
  ink: '1E293B',
  muted: '64748B',
  soft: 'F8FAFC',
  softAlt: 'F1F5F9',
  border: 'CBD5E1',
  green: '15803D',
  greenBg: 'DCFCE7',
  greenSoft: 'F0FDF4',
  amber: 'B45309',
  amberBg: 'FEF3C7',
  red: 'B91C1C',
  redBg: 'FEE2E2',
  slate: '475569',
  slateBg: 'E2E8F0',
  violet: '6D28D9',
  violetBg: 'EDE9FE',
  gold: 'A16207',
  goldBg: 'FEF9C3',
};

const FONT = 'Calibri';

const STATUS_STYLE = {
  [STATUS.FREQUENTE]: { label: 'Frequente', fg: C.green, bg: C.greenBg },
  [STATUS.COMPARECE]: { label: 'Comparece', fg: '166534', bg: C.greenSoft },
  [STATUS.IRREGULAR]: { label: 'Irregular', fg: C.amber, bg: C.amberBg },
  [STATUS.NAO_COMPARECE]: { label: 'Não comparece', fg: C.red, bg: C.redBg },
  [STATUS.NAO_INFORMADO]: { label: 'Não informado', fg: C.slate, bg: C.slateBg },
  [STATUS.DADO_INCONSISTENTE]: { label: 'Inconsistente', fg: C.violet, bg: C.violetBg },
};

const thinBorder = {
  top: { style: 'thin', color: { argb: `FF${C.border}` } },
  bottom: { style: 'thin', color: { argb: `FF${C.border}` } },
  left: { style: 'thin', color: { argb: `FF${C.border}` } },
  right: { style: 'thin', color: { argb: `FF${C.border}` } },
};

const pct = (v) => (v == null ? '—' : `${v}%`);
const catLabel = (m) => (m.categoria === 'crianca' ? 'Criança' : 'Jovem');
const bairroDe = (m) => {
  const b = String(m.bairro || '').trim();
  if (!b || ['não identificado', 'nao identificado', '—', '-'].includes(b.toLowerCase())) {
    return 'sem endereço';
  }
  return b;
};
const enderecoDe = (m) => {
  const e = String(m.endereco || '').trim();
  if (!e || ['—', '-', '--'].includes(e)) return 'sem endereço';
  return e;
};

function argb(hex) {
  return `FF${hex}`;
}

function fillSolid(hex) {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(hex) } };
}

function fontBase(overrides = {}) {
  return { name: FONT, size: 11, color: { argb: argb(C.ink) }, ...overrides };
}

function styleCell(cell, {
  fill,
  font,
  border = thinBorder,
  align = 'left',
  vertical = 'middle',
  numFmt,
} = {}) {
  if (fill) cell.fill = fillSolid(fill);
  if (font) cell.font = fontBase(font);
  else cell.font = fontBase();
  if (border) cell.border = border;
  cell.alignment = { horizontal: align, vertical, wrapText: true };
  if (numFmt) cell.numFmt = numFmt;
}

function paintBanner(ws, rowNumber, colCount, text, { fill = C.navy, fontSize = 16, height = 32 } = {}) {
  ws.mergeCells(rowNumber, 1, rowNumber, colCount);
  const cell = ws.getCell(rowNumber, 1);
  cell.value = text;
  styleCell(cell, {
    fill,
    font: { bold: true, size: fontSize, color: { argb: argb(C.white) } },
    border: {
      top: { style: 'medium', color: { argb: argb(C.navyDark) } },
      bottom: { style: 'medium', color: { argb: argb(C.navyDark) } },
      left: { style: 'medium', color: { argb: argb(C.navyDark) } },
      right: { style: 'medium', color: { argb: argb(C.navyDark) } },
    },
    align: 'left',
  });
  ws.getRow(rowNumber).height = height;
}

function paintHeaderRow(ws, rowNumber, headers) {
  const row = ws.getRow(rowNumber);
  headers.forEach((header, i) => {
    const cell = row.getCell(i + 1);
    cell.value = header;
    styleCell(cell, {
      fill: C.teal,
      font: { bold: true, size: 11, color: { argb: argb(C.white) } },
      align: 'center',
    });
  });
  row.height = 22;
}

function paintStatusCell(cell, status) {
  const cfg = STATUS_STYLE[status] || STATUS_STYLE[STATUS.NAO_INFORMADO];
  cell.value = cfg.label;
  styleCell(cell, {
    fill: cfg.bg,
    font: { bold: true, size: 11, color: { argb: argb(cfg.fg) } },
    align: 'center',
  });
}

function paintTriCell(cell, value) {
  if (value === true) {
    cell.value = 'Sim';
    styleCell(cell, {
      fill: C.greenBg,
      font: { bold: true, size: 11, color: { argb: argb(C.green) } },
      align: 'center',
    });
    return;
  }
  if (value === false) {
    cell.value = 'Não';
    styleCell(cell, {
      fill: C.redBg,
      font: { bold: true, size: 11, color: { argb: argb(C.red) } },
      align: 'center',
    });
    return;
  }
  cell.value = '—';
  styleCell(cell, {
    fill: C.softAlt,
    font: { size: 11, color: { argb: argb(C.muted) } },
    align: 'center',
  });
}

function paintDataCell(cell, value, {
  align = 'left',
  fill,
  bold = false,
  color = C.ink,
} = {}) {
  cell.value = value ?? '';
  styleCell(cell, {
    fill,
    font: { bold, size: 11, color: { argb: argb(color) } },
    align,
  });
}

function zebraFill(index) {
  return index % 2 === 0 ? C.white : C.soft;
}

function taxaFill(taxa, modo = 'comparecimento') {
  if (taxa == null) return C.softAlt;
  if (modo === 'atencao') {
    if (taxa >= 40) return C.redBg;
    if (taxa >= 20) return C.amberBg;
    return C.greenBg;
  }
  if (taxa >= 70) return C.greenBg;
  if (taxa >= 40) return C.amberBg;
  return C.redBg;
}

function setupSheet(ws, colWidths) {
  ws.properties.defaultRowHeight = 18;
  ws.views = [{ state: 'frozen', ySplit: 2, showGridLines: false }];
  colWidths.forEach((w, i) => {
    ws.getColumn(i + 1).width = w;
  });
}

async function baixarWorkbook(workbook, filename) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function criarAbaResumo(wb, { resumo, regioes, membros, atencaoMembros, jovens, semLocal, agora }) {
  const ws = wb.addWorksheet('Resumo', {
    properties: { tabColor: { argb: argb(C.navy) } },
  });
  ws.views = [{ showGridLines: false }];
  ws.getColumn(1).width = 36;
  ws.getColumn(2).width = 18;
  ws.getColumn(3).width = 40;

  paintBanner(ws, 1, 3, 'ANÁLISE TERRITORIAL DE FREQUÊNCIA', { fontSize: 18, height: 36 });
  ws.mergeCells(2, 1, 2, 3);
  styleCell(ws.getCell(2, 1), {
    fill: C.navyDark,
    font: { size: 11, color: { argb: argb(C.tealLight) } },
  });
  ws.getCell(2, 1).value = 'Sistema Reunião · Areiópolis, SP';

  ws.mergeCells(3, 1, 3, 3);
  styleCell(ws.getCell(3, 1), {
    fill: C.soft,
    font: { size: 10, italic: true, color: { argb: argb(C.muted) } },
    border: thinBorder,
  });
  ws.getCell(3, 1).value = `Gerado em: ${agora}`;

  // Seção KPIs
  paintBanner(ws, 5, 3, 'RESUMO EXECUTIVO', { fill: C.teal, fontSize: 13, height: 24 });
  paintHeaderRow(ws, 6, ['Indicador', 'Valor', 'Observação']);

  const kpis = [
    ['Total analisado', resumo.total ?? 0, 'Com filtros ativos', C.soft],
    ['Frequentes', resumo.frequentes ?? 0, 'Comparecem regularmente', C.greenBg],
    ['Comparecem', resumo.comparecem ?? 0, 'Presença confirmada', C.greenSoft],
    ['Irregulares', resumo.irregulares ?? 0, 'Frequência inconstante', C.amberBg],
    ['Não comparecem', resumo.nao_comparecem ?? 0, 'Ausência confirmada', C.redBg],
    ['Não informados', resumo.nao_informados ?? 0, 'Dados ausentes', C.slateBg],
  ];

  kpis.forEach(([ind, val, obs, fill], i) => {
    const r = 7 + i;
    paintDataCell(ws.getCell(r, 1), ind, { fill, bold: true });
    paintDataCell(ws.getCell(r, 2), val, { fill, bold: true, align: 'center' });
    paintDataCell(ws.getCell(r, 3), obs, { fill, color: C.muted });
  });

  const taxas = [
    ['Taxa de comparecimento', pct(resumo.taxa_comparecimento), 'Presença ÷ membros com dados', taxaFill(resumo.taxa_comparecimento)],
    ['Taxa de atenção', pct(resumo.taxa_atencao), 'Em atenção ÷ membros com dados', taxaFill(resumo.taxa_atencao, 'atencao')],
    ['Cobertura dos dados', pct(resumo.cobertura_dados), 'Membros com frequência informada', C.tealLight],
    ['Com localização GPS', pct(resumo.cobertura_coordenadas), 'Membros com coordenadas', C.tealLight],
  ];

  taxas.forEach(([ind, val, obs, fill], i) => {
    const r = 14 + i;
    paintDataCell(ws.getCell(r, 1), ind, { fill, bold: true });
    paintDataCell(ws.getCell(r, 2), val, { fill, bold: true, align: 'center' });
    paintDataCell(ws.getCell(r, 3), obs, { fill, color: C.muted });
  });

  paintBanner(ws, 19, 3, 'CONTEÚDO DAS ABAS', { fill: C.navy, fontSize: 13, height: 24 });
  paintHeaderRow(ws, 20, ['Aba', 'Conteúdo', 'Registros']);

  const abas = [
    ['Por Bairro', 'Métricas agregadas por região', regioes.length],
    ['Todas as Pessoas', 'Lista completa com status', membros.length],
    ['Atenção Prioritária', 'Irregulares e não comparecem', atencaoMembros.length],
    ['Jovens · Batismo', 'Jovens e situação de batismo', jovens.length],
    ['Sem Localização', 'Membros sem coordenadas GPS', semLocal.length],
  ];

  abas.forEach(([aba, conteudo, qtd], i) => {
    const r = 21 + i;
    const fill = zebraFill(i);
    paintDataCell(ws.getCell(r, 1), aba, { fill, bold: true });
    paintDataCell(ws.getCell(r, 2), conteudo, { fill });
    paintDataCell(ws.getCell(r, 3), qtd, { fill, bold: true, align: 'center' });
  });

  // Legenda de status
  paintBanner(ws, 27, 3, 'LEGENDA DE STATUS', { fill: C.slate, fontSize: 12, height: 22 });
  Object.values(STATUS).forEach((status, i) => {
    const r = 28 + i;
    const cfg = STATUS_STYLE[status];
    paintDataCell(ws.getCell(r, 1), cfg.label, {
      fill: cfg.bg,
      bold: true,
      color: cfg.fg,
      align: 'center',
    });
    paintDataCell(ws.getCell(r, 2), status, { fill: C.soft, color: C.muted, align: 'center' });
    paintDataCell(ws.getCell(r, 3), getStatusConfig(status).label, { fill: C.soft });
  });
}

function criarAbaBairros(wb, { regioes, hoje }) {
  const ws = wb.addWorksheet('Por Bairro', {
    properties: { tabColor: { argb: argb(C.teal) } },
  });
  setupSheet(ws, [26, 9, 10, 12, 12, 12, 14, 14, 14, 13, 12, 12, 12, 12]);

  const headers = [
    'Bairro', 'Total', 'C/ Dados', 'Frequentes', 'Comparecem',
    'Irregulares', 'Não comparecem', 'Não informados',
    'Taxa comparec.', 'Taxa atenção', 'Cobertura',
    'Atenção', 'Lat', 'Lng',
  ];

  paintBanner(ws, 1, headers.length, `FREQUÊNCIA POR BAIRRO · ${hoje} · ${regioes.length} regiões`);
  paintHeaderRow(ws, 2, headers);
  ws.autoFilter = { from: { row: 2, column: 1 }, to: { row: 2, column: headers.length } };

  regioes.forEach((r, i) => {
    const row = ws.getRow(3 + i);
    const fill = zebraFill(i);
    const vals = [
      r.bairro,
      r.total,
      r.com_dados_conhecidos,
      r.frequentes,
      r.comparecem,
      r.irregulares,
      r.nao_comparecem,
      r.nao_informados,
      pct(r.taxa_comparecimento),
      pct(r.taxa_atencao),
      pct(r.cobertura_dados),
      r.quantidade_atencao > 0 ? r.quantidade_atencao : 'OK',
      r.centroide?.lat ?? '',
      r.centroide?.lng ?? '',
    ];

    vals.forEach((val, c) => {
      const cell = row.getCell(c + 1);
      if (c === 8) {
        paintDataCell(cell, val, { fill: taxaFill(r.taxa_comparecimento), bold: true, align: 'center' });
      } else if (c === 9) {
        paintDataCell(cell, val, { fill: taxaFill(r.taxa_atencao, 'atencao'), bold: true, align: 'center' });
      } else if (c === 11) {
        const atFill = r.quantidade_atencao > 0 ? C.redBg : C.greenBg;
        const atColor = r.quantidade_atencao > 0 ? C.red : C.green;
        paintDataCell(cell, val, { fill: atFill, bold: true, color: atColor, align: 'center' });
      } else {
        paintDataCell(cell, val, {
          fill,
          align: c === 0 ? 'left' : 'center',
          bold: c === 0,
        });
      }
    });
  });
}

function criarAbaPessoas(wb, { membros, hoje }) {
  const ws = wb.addWorksheet('Todas as Pessoas', {
    properties: { tabColor: { argb: argb('2563EB') } },
  });
  const headers = [
    'Nome', 'Status', 'Categoria', 'Idade', 'Sexo', 'Bairro',
    'Frequente?', 'Comparece?', 'Batizado?', 'Visita indicada?', 'Aceitou visita?',
    'Endereço', 'Responsável', 'Telefone', 'Última atualização',
  ];
  setupSheet(ws, [32, 16, 11, 8, 9, 20, 11, 11, 11, 14, 14, 36, 24, 16, 16]);

  paintBanner(ws, 1, headers.length, `TODOS OS MEMBROS · ${hoje} · ${membros.length} pessoa(s)`);
  paintHeaderRow(ws, 2, headers);
  ws.autoFilter = { from: { row: 2, column: 1 }, to: { row: 2, column: headers.length } };

  membros.forEach((m, i) => {
    const row = ws.getRow(3 + i);
    const fill = zebraFill(i);
    const status = statusDoMembro(m);

    paintDataCell(row.getCell(1), m.nome || '', { fill, bold: true });
    paintStatusCell(row.getCell(2), status);
    paintDataCell(row.getCell(3), catLabel(m), {
      fill: m.categoria === 'crianca' ? 'DBEAFE' : C.goldBg,
      color: m.categoria === 'crianca' ? '1D4ED8' : C.gold,
      align: 'center',
      bold: true,
    });
    paintDataCell(row.getCell(4), m.idade ?? '', { fill, align: 'center' });
    paintDataCell(row.getCell(5), m.tipo_pessoa === 'irma' ? 'Irmã' : 'Irmão', { fill, align: 'center' });
    paintDataCell(row.getCell(6), bairroDe(m), { fill });
    paintTriCell(row.getCell(7), m.frequente_reuniao);
    paintTriCell(row.getCell(8), m.comparece_reuniao);
    if (m.categoria === 'jovem') paintTriCell(row.getCell(9), m.batizado);
    else paintDataCell(row.getCell(9), '—', { fill: C.softAlt, align: 'center', color: C.muted });
    paintTriCell(row.getCell(10), m.visita_indicada);
    paintTriCell(row.getCell(11), m.aceitou_visita);
    paintDataCell(row.getCell(12), enderecoDe(m), { fill });
    paintDataCell(row.getCell(13), m.responsavel || '', { fill });
    paintDataCell(row.getCell(14), m.responsavel_telefone || m.telefone || '', { fill });
    paintDataCell(row.getCell(15), formatarData(m.frequencia_atualizada_em), { fill, color: C.muted });
  });
}

function criarAbaAtencao(wb, { atencaoMembros, hoje }) {
  const ws = wb.addWorksheet('Atenção Prioritária', {
    properties: { tabColor: { argb: argb(C.red) } },
  });
  const headers = [
    'Nome', 'Status', 'Categoria', 'Idade', 'Bairro',
    'Responsável', 'Telefone contato', 'Endereço',
    'Visita indicada?', 'Aceitou visita?',
  ];
  setupSheet(ws, [32, 16, 11, 8, 20, 24, 16, 38, 14, 14]);

  paintBanner(ws, 1, headers.length, `ATENÇÃO PRIORITÁRIA · ${hoje} · ${atencaoMembros.length} pessoa(s)`, {
    fill: C.red,
  });
  paintHeaderRow(ws, 2, headers);
  ws.getRow(2).eachCell((cell) => {
    cell.fill = fillSolid('991B1B');
  });
  ws.autoFilter = { from: { row: 2, column: 1 }, to: { row: 2, column: headers.length } };

  atencaoMembros.forEach((m, i) => {
    const row = ws.getRow(3 + i);
    const fill = i % 2 === 0 ? 'FFF7F7' : C.redBg;
    const status = statusDoMembro(m);

    paintDataCell(row.getCell(1), m.nome || '', { fill, bold: true });
    paintStatusCell(row.getCell(2), status);
    paintDataCell(row.getCell(3), catLabel(m), { fill, align: 'center' });
    paintDataCell(row.getCell(4), m.idade ?? '', { fill, align: 'center' });
    paintDataCell(row.getCell(5), bairroDe(m), { fill });
    paintDataCell(row.getCell(6), m.responsavel || '', { fill });
    paintDataCell(row.getCell(7), m.responsavel_telefone || m.telefone || '', { fill });
    paintDataCell(row.getCell(8), enderecoDe(m), { fill });
    paintTriCell(row.getCell(9), m.visita_indicada);
    paintTriCell(row.getCell(10), m.aceitou_visita);
  });
}

function criarAbaJovens(wb, { jovens, jBat, jNBat, jNI, hoje }) {
  const ws = wb.addWorksheet('Jovens · Batismo', {
    properties: { tabColor: { argb: argb(C.gold) } },
  });
  const headers = [
    'Nome', 'Status frequência', 'Idade', 'Bairro', 'Batizado?',
    'Visita indicada?', 'Aceitou visita?', 'Responsável', 'Telefone', 'Endereço',
  ];
  ws.properties.defaultRowHeight = 18;
  ws.views = [{ state: 'frozen', ySplit: 3, showGridLines: false }];
  [32, 16, 8, 20, 12, 14, 14, 24, 16, 36].forEach((w, i) => {
    ws.getColumn(i + 1).width = w;
  });

  paintBanner(ws, 1, headers.length, `JOVENS E BATISMO · ${hoje} · ${jovens.length} jovem(ns)`, {
    fill: C.gold,
  });

  ws.mergeCells(2, 1, 2, headers.length);
  const sumCell = ws.getCell(2, 1);
  sumCell.value = `Batizados: ${jBat}   |   Não batizados: ${jNBat}   |   Não informado: ${jNI}`;
  styleCell(sumCell, {
    fill: C.goldBg,
    font: { bold: true, size: 11, color: { argb: argb(C.gold) } },
  });
  ws.getRow(2).height = 22;

  paintHeaderRow(ws, 3, headers);
  ws.getRow(3).eachCell((cell) => {
    cell.fill = fillSolid('92400E');
  });
  ws.autoFilter = { from: { row: 3, column: 1 }, to: { row: 3, column: headers.length } };

  const ordenados = [...jovens].sort((a, b) => {
    const ord = (m) => (m.batizado === false ? 0 : m.batizado == null ? 1 : 2);
    return ord(a) - ord(b) || (a.nome || '').localeCompare(b.nome || '', 'pt-BR');
  });

  ordenados.forEach((m, i) => {
    const row = ws.getRow(4 + i);
    const fill = zebraFill(i);
    paintDataCell(row.getCell(1), m.nome || '', { fill, bold: true });
    paintStatusCell(row.getCell(2), statusDoMembro(m));
    paintDataCell(row.getCell(3), m.idade ?? '', { fill, align: 'center' });
    paintDataCell(row.getCell(4), bairroDe(m), { fill });
    paintTriCell(row.getCell(5), m.batizado);
    paintTriCell(row.getCell(6), m.visita_indicada);
    paintTriCell(row.getCell(7), m.aceitou_visita);
    paintDataCell(row.getCell(8), m.responsavel || '', { fill });
    paintDataCell(row.getCell(9), m.responsavel_telefone || m.telefone || '', { fill });
    paintDataCell(row.getCell(10), enderecoDe(m), { fill });
  });
}

function criarAbaSemLocal(wb, { semLocal }) {
  const ws = wb.addWorksheet('Sem Localização', {
    properties: { tabColor: { argb: argb(C.slate) } },
  });
  const headers = [
    'Nome', 'Status', 'Categoria', 'Idade', 'Bairro informado',
    'Responsável', 'Telefone', 'Endereço textual',
  ];
  setupSheet(ws, [32, 16, 11, 8, 20, 24, 16, 40]);

  paintBanner(ws, 1, headers.length, `SEM LOCALIZAÇÃO GPS · ${semLocal.length} membro(s)`, {
    fill: C.slate,
  });
  paintHeaderRow(ws, 2, headers);
  ws.autoFilter = { from: { row: 2, column: 1 }, to: { row: 2, column: headers.length } };

  if (!semLocal.length) {
    paintDataCell(ws.getCell(3, 1), 'Nenhum membro sem localização com os filtros atuais.', {
      fill: C.soft,
      color: C.muted,
    });
    ws.mergeCells(3, 1, 3, headers.length);
    return;
  }

  semLocal.forEach((m, i) => {
    const row = ws.getRow(3 + i);
    const fill = zebraFill(i);
    paintDataCell(row.getCell(1), m.nome || '', { fill, bold: true });
    paintStatusCell(row.getCell(2), statusDoMembro(m));
    paintDataCell(row.getCell(3), catLabel(m), { fill, align: 'center' });
    paintDataCell(row.getCell(4), m.idade ?? '', { fill, align: 'center' });
    paintDataCell(row.getCell(5), bairroDe(m), { fill });
    paintDataCell(row.getCell(6), m.responsavel || '', { fill });
    paintDataCell(row.getCell(7), m.responsavel_telefone || m.telefone || '', { fill });
    paintDataCell(row.getCell(8), enderecoDe(m), { fill });
  });
}

/**
 * Gera e baixa planilha premium da Análise Territorial de Frequência.
 * @param {object} opts
 * @param {string[]} [opts.abas] ids das abas a incluir (padrão: todas)
 */
export async function exportarAnaliseFrequenciaExcel({
  membros = [],
  regioes = [],
  resumo = {},
  abas = ['resumo', 'bairros', 'pessoas', 'atencao', 'jovens', 'sem_local'],
}) {
  const incluir = new Set(abas);
  if (!incluir.size) throw new Error('Selecione ao menos uma aba para exportar.');

  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Sistema Reunião';
  workbook.created = new Date();
  workbook.modified = new Date();
  workbook.lastPrinted = new Date();

  const hoje = new Date().toLocaleDateString('pt-BR');
  const agora = new Date().toLocaleString('pt-BR');

  const atencaoMembros = [...membros]
    .filter((m) => [STATUS.IRREGULAR, STATUS.NAO_COMPARECE].includes(statusDoMembro(m)))
    .sort((a, b) => getStatusConfig(statusDoMembro(a)).order - getStatusConfig(statusDoMembro(b)).order);

  const jovens = membros.filter((m) => m.categoria === 'jovem');
  const jBat = jovens.filter((m) => m.batizado === true).length;
  const jNBat = jovens.filter((m) => m.batizado === false).length;
  const jNI = jovens.filter((m) => m.batizado == null).length;
  const semLocal = membros.filter((m) => !temCoordenadas(m));

  if (incluir.has('resumo')) {
    criarAbaResumo(workbook, {
      resumo, regioes, membros, atencaoMembros, jovens, semLocal, agora,
    });
  }
  if (incluir.has('bairros')) criarAbaBairros(workbook, { regioes, hoje });
  if (incluir.has('pessoas')) criarAbaPessoas(workbook, { membros, hoje });
  if (incluir.has('atencao')) criarAbaAtencao(workbook, { atencaoMembros, hoje });
  if (incluir.has('jovens')) criarAbaJovens(workbook, { jovens, jBat, jNBat, jNI, hoje });
  if (incluir.has('sem_local')) criarAbaSemLocal(workbook, { semLocal });

  if (!workbook.worksheets.length) {
    throw new Error('Nenhuma aba pôde ser gerada com a seleção atual.');
  }

  const filename = `analise_frequencia_${new Date().toISOString().slice(0, 10)}.xlsx`;
  await baixarWorkbook(workbook, filename);

  return {
    pessoas: membros.length,
    regioes: regioes.length,
    atencao: atencaoMembros.length,
    abas: workbook.worksheets.length,
  };
}
